import { useTranslation } from 'react-i18next';
import styles from './MarketSearch.module.css';

const SearchIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/**
 * The magnifier that opens the search: it sits first in the row of section
 * chips (modern) or the offer's toolbar (classic), so the search costs no
 * line of its own until it is used.
 */
export const MarketSearchToggle = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={styles.toggle}
      aria-label={t('market_search_placeholder')}
      title={t('market_search_placeholder')}
      onClick={onClick}
    >
      <SearchIcon className={styles.icon} />
    </button>
  );
};

/**
 * The search field over a match's offer: type "hend", "gol", "korn" and only
 * those markets stay. Shows how many markets match; × clears the query and
 * closes the field.
 */
export const MarketSearch = ({
  value,
  onChange,
  found,
  onClose,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Markets matching the query; shown once something is typed. */
  found: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const active = value.trim() !== '';
  return (
    <div className={styles.search} data-active={active || undefined}>
      <SearchIcon className={styles.icon} />
      <input
        // Opened by a tap on the magnifier: straight to typing.
        autoFocus
        type="search"
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        placeholder={t('market_search_placeholder')}
        aria-label={t('market_search_placeholder')}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="search"
      />
      {active && (
        <span className={styles.count} aria-live="polite">
          {found}
        </span>
      )}
      <button
        type="button"
        className={styles.clear}
        aria-label={t('market_search_clear')}
        onClick={() => {
          onChange('');
          onClose();
        }}
      >
        ×
      </button>
    </div>
  );
};

/** Said in place of the offer when nothing matches the query. */
export const MarketSearchEmpty = ({ query }: { query: string }) => {
  const { t } = useTranslation();
  return (
    <p className={styles.empty}>
      {t('market_search_empty', { query: query.trim() })}
    </p>
  );
};
